import { apiFetch, safeFetch } from "./client";
import type { Email, CheckResult } from "../types";

export async function listEmails(
  matterId: string,
  page = 1,
  limit = 50,
): Promise<{ emails: Email[]; total: number; page: number; limit: number }> {
  const r = await safeFetch<{ emails: Email[]; total: number; page: number; limit: number }>(
    `/matters/${matterId}/emails?page=${page}&limit=${limit}`,
  );
  return r ?? { emails: [], total: 0, page, limit };
}

export async function getEmail(id: string): Promise<Email | null> {
  const r = await safeFetch<{ email: Email }>(`/emails/${id}`);
  return r?.email ?? null;
}

export interface ImportEmailInput {
  from: string;
  fromName?: string;
  to?: string[];
  cc?: string[];
  subject?: string;
  time?: string;
  raw?: string;
  body?: string;
  /** Simulation metadata (local id, replay seed, expectations) persisted alongside the row. */
  simMeta?: unknown;
  attachments?: Array<{
    filename: string;
    doc_type?: string;
    version?: number;
    content?: string;
    fileData?: string;
  }>;
}

export async function createEmail(matterId: string, input: ImportEmailInput): Promise<Email | null> {
  const r = await safeFetch<{ email: Email }>(`/matters/${matterId}/emails`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return r?.email ?? null;
}

export interface ImportEmailsResult {
  ok: boolean;
  imported: number;
  ids: string[];
  /** Per imported email (same order as ids): created attachment rows. */
  attachments?: Array<Array<{ id: string; filename: string; version: number }>>;
  failed?: Array<{ index: number; error: string }>;
  error?: string;
  warnings?: Array<{ index: number; warning: string }>;
}

export async function importEmails(
  matterId: string,
  emails: ImportEmailInput[],
): Promise<ImportEmailsResult | null> {
  return safeFetch(`/matters/${matterId}/emails/import`, {
    method: "POST",
    body: JSON.stringify(emails),
  });
}

export async function updateEmail(id: string, input: ImportEmailInput): Promise<Email | null> {
  const r = await safeFetch<{ email: Email }>(`/emails/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return r?.email ?? null;
}

export async function deleteEmail(id: string): Promise<boolean> {
  try {
    await apiFetch(`/emails/${id}`, { method: "DELETE" });
    return true;
  } catch {
    return false;
  }
}

export async function getEmailResults(emailId: string): Promise<CheckResult[]> {
  const r = await safeFetch<{ results: CheckResult[] }>(`/emails/${emailId}/results`);
  return r?.results ?? [];
}
